/**
 * INNFLOW DESIGN SYSTEM V2.0 - SearchInput Component
 * 
 * Input de busca com autocomplete e debounce
 * 
 * @version 2.0.0
 * @author INNFLOW Design Team
 */

import React, { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDebounce } from '@/hooks/useDebounce';
import { Icon } from '@/components/atoms/Icon';
import { EmptyState } from './EmptyState';
import './SearchInput.css';

export interface SearchResult {
  id: string;
  label: string;
  description?: string;
  [key: string]: unknown;
}

export interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  onSearch?: (query: string) => Promise<SearchResult[]> | SearchResult[];
  placeholder?: string;
  loading?: boolean;
  onClear?: () => void;
  onSelect?: (result: SearchResult) => void;
  className?: string;
  'aria-label'?: string;
}

/**
 * SearchInput - Input de busca com autocomplete
 * 
 * Features:
 * - Debounce para performance (300ms)
 * - Autocomplete dropdown com glassmorphism
 * - Keyboard navigation (arrow keys, enter, escape)
 * - Highlight de resultados
 * - Loading state
 * - Empty state
 * - Caret verde #19F58C
 */
export const SearchInput: React.FC<SearchInputProps> = ({
  value,
  onChange,
  onSearch,
  placeholder = 'Buscar...',
  loading = false,
  onClear,
  onSelect,
  className,
  'aria-label': ariaLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debouncedValue = useDebounce(value, 300);

  useEffect(() => {
    if (debouncedValue && onSearch) {
      setIsSearching(true);
      Promise.resolve(onSearch(debouncedValue))
        .then((searchResults) => {
          setResults(searchResults);
          setIsSearching(false);
          setIsOpen(true);
        })
        .catch(() => {
          setIsSearching(false);
        });
    } else {
      setResults([]);
      setIsOpen(false);
    }
  }, [debouncedValue, onSearch]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && results[highlightedIndex]) {
          handleSelect(results[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  const handleSelect = (result: SearchResult) => {
    onChange(result.label);
    onSelect?.(result);
    setIsOpen(false);
    setHighlightedIndex(-1);
    inputRef.current?.blur();
  };

  const handleClear = () => {
    onChange('');
    onClear?.();
    setIsOpen(false);
    setResults([]);
    inputRef.current?.focus();
  };

  const highlightText = (text: string, query: string) => {
    if (!query) return text;
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, index) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <mark key={index} className="innflow-search-highlight">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  return (
    <div className={cn('innflow-search-wrapper', className)}>
      <div
        className={cn('innflow-search', {
          'innflow-search--loading': loading || isSearching,
          'innflow-search--open': isOpen,
        })}
      >
        <Search className="innflow-search__icon" size={16} />
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => {
            if (results.length > 0) setIsOpen(true);
          }}
          onBlur={() => {
            // Delay para permitir clique no dropdown
            setTimeout(() => setIsOpen(false), 200);
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="innflow-search__input"
          aria-label={ariaLabel || 'Buscar'}
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-controls="innflow-search-results"
          role="combobox"
        />
        {(loading || isSearching) && (
          <Loader2 className="innflow-search__spinner" size={16} />
        )}
        {value && !loading && !isSearching && (
          <button
            type="button"
            onClick={handleClear}
            className="innflow-search__clear"
            aria-label="Limpar busca"
          >
            <X size={16} />
          </button>
        )}
      </div>
      {isOpen && (
        <div
          ref={dropdownRef}
          id="innflow-search-results"
          className="innflow-search-dropdown"
          role="listbox"
        >
          {isSearching ? (
            <div className="innflow-search-dropdown__loading">Buscando...</div>
          ) : results.length > 0 ? (
            results.map((result, index) => (
              <div
                key={result.id}
                className={cn('innflow-search-result', {
                  'innflow-search-result--highlighted': index === highlightedIndex,
                })}
                onClick={() => handleSelect(result)}
                onMouseEnter={() => setHighlightedIndex(index)}
                role="option"
                aria-selected={index === highlightedIndex}
              >
                <div className="innflow-search-result__label">
                  {highlightText(result.label, value)}
                </div>
                {result.description && (
                  <div className="innflow-search-result__description">{result.description}</div>
                )}
              </div>
            ))
          ) : (
            <EmptyState
              title="Nenhum resultado encontrado"
              description="Tente buscar com outros termos"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default SearchInput;
